<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { TriangleAlert } from '@lucide/vue'
import { BILLING_LOCATION } from '@/app/route-names'
import { Button } from '@/components/ui/button'
import { useCanViewBilling } from '@/features/billing/composables/useCanViewBilling'
import { useHasNoSubscription } from '@/features/billing/composables/useHasNoSubscription'
import { useWhiteLabelTrialOnHold } from '@/features/billing/composables/useWhiteLabelTrialOnHold'
import { useOrganizationOnboardingStep } from '@/features/organizations/composables/useOrganizationOnboardingStep'
import { can } from '@/lib/permissions/can'
import { Permission } from '@/lib/permissions/keys'

const { t } = useI18n()
const hasNoSubscription = useHasNoSubscription()
const canViewBilling = useCanViewBilling()
const trialOnHold = useWhiteLabelTrialOnHold()
// The onboarding card already asks for the website request.
const { isCardPossible } = useOrganizationOnboardingStep()
</script>

<template>
  <div
    v-if="trialOnHold && !isCardPossible"
    role="status"
    class="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3"
  >
    <TriangleAlert class="size-4 shrink-0 text-amber-500" />
    <p class="min-w-0 flex-1 text-sm">
      {{
        can(Permission.WebsiteCreate)
          ? t('billing.trialOnHoldBanner.message')
          : t('billing.trialOnHoldBanner.askAdmin')
      }}
    </p>
    <Button v-if="can(Permission.WebsiteCreate)" as-child size="sm" variant="outline">
      <RouterLink to="/websites/requests/new">{{ t('billing.trialOnHoldBanner.cta') }}</RouterLink>
    </Button>
  </div>
  <div
    v-else-if="hasNoSubscription"
    role="status"
    class="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3"
  >
    <TriangleAlert class="size-4 shrink-0 text-amber-500" />
    <p class="min-w-0 flex-1 text-sm">
      {{
        canViewBilling
          ? t('billing.noSubscriptionBanner.message')
          : t('billing.noSubscriptionBanner.askAdmin')
      }}
    </p>
    <Button v-if="canViewBilling" as-child size="sm" variant="outline">
      <RouterLink :to="BILLING_LOCATION">{{ t('billing.noSubscriptionBanner.cta') }}</RouterLink>
    </Button>
  </div>
</template>
